import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IAllDirectorsList, TApprovalStatus } from '../../../types/addDirector';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IDirectorParams {
  approvalStatus?: TApprovalStatus;
}

export interface IGetDirectorAdditionalInfoResponse {
  status?: boolean;
  message?: string;
  data?: IAllDirectorsList[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetDirectorAdditionalInfoResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getDirectorAdditionalInfoAsync = createAsyncThunk(
  'businessDirectors/additionalInfo',
  async (values: IDirectorParams, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(
        `/business-directors?businessId=${businessId}${
          values.approvalStatus
            ? `&approvalStatus=${values.approvalStatus}`
            : ''
        }`,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const directorsAdditionalInfoSlice = createSlice({
  name: 'directors/additional-info',
  initialState,
  reducers: {
    resetGetDirectorAdditionalInfo: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDirectorAdditionalInfoAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getDirectorAdditionalInfoAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetDirectorAdditionalInfoResponse;
      })
      .addCase(getDirectorAdditionalInfoAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetDirectorAdditionalInfoResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetDirectorAdditionalInfo } =
  directorsAdditionalInfoSlice.actions;

export default directorsAdditionalInfoSlice.reducer;
